import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { getFirstName } from '@components/dashboard/data/Specialists';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import SpecialistHelpsWithTags from '@components/shared/SpecialistHelpsWIthTags';
import { BREAKPOINT } from '@constants';
import { VitalsAuthorNode } from '@contracts';

interface Props {
  author: VitalsAuthorNode;
}

const AuthorData: React.FC<Props> = ({ author }) => {
  const {
    data: { name, professional_title, author_bio, image, tags }
  } = author;

  const bioTextSerializer = {
    paragraph: ({ children }: { children: React.ReactNode }) => <Typography $variant={TextVariant.BodyRegularLG}>{children}</Typography>
  };

  return (
    <>
      <Container>
        <AuthorInfoContainer>
          {image.gatsbyImageData ? (
            <ImageContainer>
              <GatsbyImage image={image.gatsbyImageData} alt={image.alt ?? name ?? ''} loading="eager" />
            </ImageContainer>
          ) : null}
          <AuthorInfo>
            <Titles>
              <Typography $variant={TextVariant.Heading3}>{name}</Typography>
              <Typography $variant={TextVariant.UpperText4}>{professional_title}</Typography>
            </Titles>
            <Bio>
              <PrismicRichText field={author_bio.richText} components={bioTextSerializer} />
            </Bio>
          </AuthorInfo>
          {tags && tags.length > 0 ? (
            <TagsContainer>
              <Tags>
                <SpecialistHelpsWithTags
                  name={getFirstName(author.data)}
                  tags={tags}
                  HeadingComponent={(props) => <Typography $variant={TextVariant.UpperText4} {...props} />}
                />
              </Tags>
            </TagsContainer>
          ) : null}
        </AuthorInfoContainer>
      </Container>
    </>
  );
};

export default AuthorData;

const Container = styled.div`
  width: 100%;
  display: flex;
  padding: 32px 120px 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 56px;

  p {
    color: ${Colors.navyBlue};
  }

  @media screen and (max-width: ${BREAKPOINT.tablet}px) {
    padding: 30px 64px;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    align-self: stretch;
  }
  @media screen and (max-width: ${BREAKPOINT.mobileL}px) {
    padding: 30px 16px;
  }
`;

const AuthorInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  gap: 32px;

  @media screen and (max-width: ${BREAKPOINT.laptop}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    align-self: stretch;
  }
`;

const ImageContainer = styled.div`
  flex: 1 0 0;
  max-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 21px;

  .gatsby-image-wrapper {
    width: 200px;
    height: 200px;
    border-radius: 100%;
    overflow: hidden;
    
    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
    }
`;

const AuthorInfo = styled.div`
  flex: 2 0 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bio = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const TagsContainer = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Tags = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
